import React, { useState } from "react";
import { api } from "../../lib/api";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const ORIGINS = ["Miami", "Montreal", "Santiago", "Port-au-Prince", "Cap-Haitien"];
const CARGOS = ["Box", "Barrel", "Electronics", "Documents", "Other"];
const METHODS = ["Air Freight", "Sea Freight"];

export default function NewShipment() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    sender_name: "", sender_email: "", sender_phone: "", sender_address: "", sender_city: "",
    receiver_name: "", receiver_phone: "", receiver_address: "", receiver_city: "",
    cargo_type: "Box", method: "Sea Freight",
    origin_city: "Miami", destination_city: "Port-au-Prince",
    weight_lbs: "", preferred_language: "ht",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sender_name || !form.receiver_name) {
      toast.error("Sender and receiver name are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, weight_lbs: form.weight_lbs ? parseFloat(form.weight_lbs) : null };
      const { data } = await api.post("/shipments", payload);
      toast.success(`Manifest created: ${data.tracking_id}`);
      navigate(`/label/${data.tracking_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create shipment");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-3 py-2 border border-[#1B3022]/30 bg-white rounded-sm text-sm focus:outline-none focus:border-[#D4AF37]";

  return (
    <div data-testid="new-shipment-page" className="max-w-4xl">
      <h1 className="font-serif-bv text-3xl text-[#1B3022]">New Shipment</h1>
      <p className="text-sm text-[#1B3022]/60 mb-8">Create a manifest. An HBS tracking number will be auto-assigned.</p>

      <form onSubmit={submit} data-testid="new-shipment-form" className="bg-white border border-[#1B3022]/15 rounded-sm p-6 lg:p-8 grid gap-8">
        <div>
          <h3 className="font-serif-bv text-lg text-[#1B3022] mb-3">Sender</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="f-sender-name" required placeholder="Full name *" value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} className={inputCls} />
            <input data-testid="f-sender-email" type="email" placeholder="Email" value={form.sender_email} onChange={(e) => set("sender_email", e.target.value)} className={inputCls} />
            <input placeholder="Phone" value={form.sender_phone} onChange={(e) => set("sender_phone", e.target.value)} className={inputCls} />
            <input placeholder="Street address" value={form.sender_address} onChange={(e) => set("sender_address", e.target.value)} className={inputCls} />
            <input placeholder="City / State / Zip" value={form.sender_city} onChange={(e) => set("sender_city", e.target.value)} className={inputCls} />
            <select value={form.origin_city} onChange={(e) => set("origin_city", e.target.value)} className={inputCls}>
              {ORIGINS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="border-t border-[#1B3022]/10 pt-6">
          <h3 className="font-serif-bv text-lg text-[#1B3022] mb-3">Receiver</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="f-receiver-name" required placeholder="Full name *" value={form.receiver_name} onChange={(e) => set("receiver_name", e.target.value)} className={inputCls} />
            <input placeholder="Phone (+509...)" value={form.receiver_phone} onChange={(e) => set("receiver_phone", e.target.value)} className={inputCls} />
            <input placeholder="Street address" value={form.receiver_address} onChange={(e) => set("receiver_address", e.target.value)} className={inputCls} />
            <input placeholder="City" value={form.receiver_city} onChange={(e) => set("receiver_city", e.target.value)} className={inputCls} />
            <select value={form.destination_city} onChange={(e) => set("destination_city", e.target.value)} className={inputCls}>
              {ORIGINS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="border-t border-[#1B3022]/10 pt-6">
          <h3 className="font-serif-bv text-lg text-[#1B3022] mb-3">Package</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <select value={form.cargo_type} onChange={(e) => set("cargo_type", e.target.value)} className={inputCls}>
              {CARGOS.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select value={form.method} onChange={(e) => set("method", e.target.value)} className={inputCls}>
              {METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
            <input type="number" min="0" placeholder="Weight (lbs)" value={form.weight_lbs} onChange={(e) => set("weight_lbs", e.target.value)} className={inputCls} />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => navigate(-1)} className="px-5 py-2 text-xs text-[#1B3022]/70 uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="submit-shipment" className="btn-gold rounded-sm px-6 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-60">
            {submitting ? <Loader2 className="animate-spin" size={14} /> : null}
            Create & generate label
          </button>
        </div>
      </form>
    </div>
  );
}
